import React, { useEffect, useRef } from 'react';
import { IonPage, IonRouterOutlet, useIonViewWillLeave } from '@ionic/react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { PhoneLogic } from '@/modules/phone';
import { useUserContext } from '@/modules/user/useUserContext';
import { useIonRouter } from '@ionic/react';
import {
  ProfileWizardProvider,
  useProfileWizardContext,
} from '@/contexts/ProfileWizardContext';

import WelcomeStep from './WelcomeStep';
import AttributionStep from './AttributionStep';
import PhoneStep from './PhoneStep';
import PhoneMigrationProofStep from './PhoneMigrationProofStep';
import PhoneMigrationOldPhoneStep from './PhoneMigrationOldPhoneStep';
import PhoneMigrationEmailStep from './PhoneMigrationEmailStep';
import PhoneMigrationQuestionsStep from './PhoneMigrationQuestionsStep';
import PhoneMigrationNewPhoneStep from './PhoneMigrationNewPhoneStep';
import EmailStep from './EmailStep';
import SummaryStep from './SummaryStep';

// Component to handle redirect logic
const ProfileWizardRedirect: React.FC = () => {
  const router = useIonRouter();
  const location = useLocation();
  const queryString = (
    router.routeInfo.search ||
    location.search ||
    ''
  ).replace(/^\?/, '');
  const redirectTo = queryString
    ? `/profile/steps/welcome?${queryString}`
    : '/profile/steps/welcome';
  return <Redirect to={redirectTo} />;
};

const ProfileWizardContent: React.FC = () => {
  const location = useLocation<{ from?: string }>();
  const {
    phone: contextPhone,
    email: contextEmail,
    loading: userLoading,
  } = useUserContext();
  const { setPhone, setEmail, setOriginalPhone, setOriginalEmail } =
    useProfileWizardContext();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Track if initialization has occurred to prevent race conditions
  const hasInitialized = useRef(false);

  // Seed the wizard's phone/email input buffers + change-detection snapshots from
  // context ONCE, after UserContext has hydrated. (Attribution is not seeded
  // here — it lives in UserContext directly.) On a reload mid-wizard, UserContext restores
  // phone/email from storage asynchronously, so they're momentarily undefined
  // while it loads; seeding then would lock in blank values (hasInitialized) and
  // never pick up the restored profile until the wizard is reopened. Gating on
  // !userLoading defers the one-time seed until the values are available.
  useEffect(() => {
    if (userLoading || hasInitialized.current) return;

    setPhone(contextPhone ? PhoneLogic.format(contextPhone.value) : '');
    setEmail(contextEmail || '');
    setOriginalPhone(contextPhone ? PhoneLogic.format(contextPhone.value) : '');
    setOriginalEmail(contextEmail || '');

    hasInitialized.current = true;
    // Re-run when loading flips false, or when query params arrive after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, userLoading]);

  // The toolbar, per-step title, progress bar, and Close button now live in the
  // shared ProfileWizardHeader that each step renders (mirroring the Share
  // Wizard) — the shell is just the page + routing outlet.
  return (
    <IonPage className="profile-wizard">
      <IonRouterOutlet className="profile-wizard__outlet">
        {/* Root redirect - determine where to start */}
        <Route exact path="/profile" component={ProfileWizardRedirect} />

        {/* Step routes */}
        <Route exact path="/profile/steps/welcome" component={WelcomeStep} />
        <Route
          exact
          path="/profile/steps/attribution"
          component={AttributionStep}
        />
        <Route exact path="/profile/steps/phone" component={PhoneStep} />
        <Route
          exact
          path="/profile/steps/phone-migration-proof"
          component={PhoneMigrationProofStep}
        />
        <Route
          exact
          path="/profile/steps/phone-migration-old-phone"
          component={PhoneMigrationOldPhoneStep}
        />
        <Route
          exact
          path="/profile/steps/phone-migration-email"
          component={PhoneMigrationEmailStep}
        />
        <Route
          exact
          path="/profile/steps/phone-migration-questions"
          component={PhoneMigrationQuestionsStep}
        />
        <Route
          exact
          path="/profile/steps/phone-migration-new-phone"
          component={PhoneMigrationNewPhoneStep}
        />
        <Route exact path="/profile/steps/email" component={EmailStep} />
        <Route exact path="/profile/steps/summary" component={SummaryStep} />
      </IonRouterOutlet>
    </IonPage>
  );
};

// Wrapper component with provider
const ProfileWizardRouter: React.FC = () => {
  const { refreshUserContext } = useUserContext();

  return (
    <ProfileWizardProvider refreshUserContext={refreshUserContext}>
      <ProfileWizardContent />
    </ProfileWizardProvider>
  );
};

export default ProfileWizardRouter;
